/**
 * かき出し依頼（/exports/<タブ>）。ジョブ（export_requests）を種類ごとのタブに分ける: すべて / 演出の動画 / AviUtl /
 * フィールド動画 / カード / その他。帯は撮影・カード管理と同じ `.subnav`。タブには待機中・処理中の数を出す。
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../appInfo';
import { api } from '../api/api';
import { OneDriveUploadButton } from '../components/OneDrivePanel';
import { formatServerTime } from '../utils/serverTime';

const EXPORT_STATUS_LABEL = {
  pending:    '待機中',
  processing: '処理中',
  completed:  '完了',
  failed:     '失敗',
  cancelled:  'キャンセル済',
};

const EXPORT_STATUS_BADGE = {
  pending:    { background: '#5a4e00', color: '#f5d65e' },
  processing: { background: '#1e6b3a', color: '#6ee89a' },
  completed:  { background: '#444',    color: '#aaa' },
  failed:     { background: '#6b1e1e', color: '#e89a9a' },
  cancelled:  { background: '#3a3a3a', color: '#888' },
};

const KIND_LABEL = {
  aviutl_output: 'AviUtl の出力（旧）',   // Issue #998 で消した kind（kind の無い古い行も）。過去の行の表示に残す
  field_gen:     'フィールド動画生成',
  archive_duel:  'アーカイブ',
  duel_sources:  'シリーズ共通ソース',   // Issue #756 で消した kind。過去の行の表示に残す
  speech_review: '発話区間レビュー(LLM)',
  fx_export:     '演出の書き出し',
  fx_export_v2:  '演出の書き出し（ゲームサーバー）',   // Issue #768
  hand_export:   '手札（追従前）',   // Issue #942。積むのはデュエルのページ（HandExportPanel）
  godot_aviutl_output: 'AviUtl の出力（Godot）',   // Issue #969。積むのはデュエルの一覧の試合ごとの欄
  godot_cut_output: '発話と演出だけ（Godot）',   // Issue #976。積むのはデュエルの一覧の試合ごとの欄・Godot の位置合わせの画面
  onedrive_cards: 'OneDrive へカード画像',   // Issue #943
  onedrive_video: 'OneDrive へ動画',
  onedrive_app:   'OneDrive へアプリ',   // Issue #991。見回りが Windows 版の新しい版を見つけたら
  card_code_read: 'カードの読み取り',   // Issue #929
  card_scan_check: '傾きの測定',        // Issue #932
  card_packs_sync: '型番の取得',        // Issue #951
};

// 種類ごとのタブ。kinds に無い kind は「その他」
export const EXPORT_TABS = [
  { id: 'all', label: 'すべて' },
  { id: 'fx', label: '演出の動画', kinds: ['fx_export', 'fx_export_v2', 'hand_export', 'onedrive_video'] },
  { id: 'aviutl', label: 'AviUtl', kinds: ['aviutl_output', 'godot_aviutl_output', 'godot_cut_output'] },
  { id: 'field', label: 'フィールド動画', kinds: ['field_gen'] },
  { id: 'cards', label: 'カード', kinds: ['card_code_read', 'card_scan_check', 'card_packs_sync', 'onedrive_cards'] },
  { id: 'other', label: 'その他' },
];
const GROUPED = new Set(EXPORT_TABS.flatMap(t => t.kinds ?? []));
export function inTab(tabId, kind) {
  if (tabId === 'all') return true;
  if (tabId === 'other') return !GROUPED.has(kind ?? 'aviutl_output');
  return (EXPORT_TABS.find(t => t.id === tabId)?.kinds ?? []).includes(kind ?? 'aviutl_output');
}
const isActive = (r) => r.status === 'pending' || r.status === 'processing';

// 書き出した動画を OneDrive へ上げられる kind（本番の onedrive/jobs.py の VIDEO_KINDS と同じ。Issue #943）
export const ONEDRIVE_KINDS = ['hand_export'];   // 本番の onedrive/jobs.py の VIDEO_KINDS と同じ（fx_export / fx_export_v2 は #981 で消した）

function RequestCard({ req, duelName, cancelling, onCancel, retrying, onRetry }) {
  const [videoOpen, setVideoOpen] = useState(false);
  const baseLabel = KIND_LABEL[req.kind ?? 'aviutl_output'] ?? req.kind;
  // 手札は席ごとのジョブ（params の player_number）
  const handPlayer = req.kind === 'hand_export' ? req.runs?.player_number : null;
  const kindLabel = handPlayer ? `${baseLabel} P${handPlayer}` : baseLabel;

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.85rem', color: '#666' }}>#{req.id}</span>
        <span style={{
          ...EXPORT_STATUS_BADGE[req.status],
          display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem',
        }}>
          {EXPORT_STATUS_LABEL[req.status] ?? req.status}
        </span>
        <span className="card-title" style={{ fontSize: '0.95rem' }}>{duelName}</span>
        {kindLabel && (
          <span style={{ fontSize: '0.8rem', color: '#8a8' }}>{kindLabel}</span>
        )}
        {req.worker_id && (
          <span style={{ fontSize: '0.8rem', color: '#666' }}>{req.worker_id}</span>
        )}
        {/* runs は（消した）aviutl_output の過去の行だけ配列。field_gen などの params は dict なので出さない */}
        {Array.isArray(req.runs) && (
          <span style={{ fontSize: '0.8rem', color: '#6699cc' }}>runs×{req.runs.length}</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#555' }}>
          {formatServerTime(req.created_at)}
        </span>
        {req.result_video_url && (
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setVideoOpen(v => !v)}
            style={{ marginLeft: 4 }}
          >
            {videoOpen ? '動画を閉じる' : '動画を確認'}
          </button>
        )}
        {req.result_video_url && req.status === 'completed' && (
          // 上げるときに Content-Disposition: attachment を付けてあるので、開くと保存になる（Issue #942）
          <a className="btn btn-sm btn-secondary" href={req.result_video_url} download style={{ marginLeft: 4 }}>
            ダウンロード
          </a>
        )}
        {req.result_video_url && req.status === 'completed' && ONEDRIVE_KINDS.includes(req.kind) && (
          <OneDriveUploadButton requestId={req.id} />
        )}
        {(req.status === 'pending' || req.status === 'processing') && (
          <button
            className="btn btn-sm btn-danger"
            onClick={() => onCancel(req.id)}
            disabled={cancelling === req.id}
            style={{ marginLeft: 4 }}
          >
            {cancelling === req.id ? '...' : 'キャンセル'}
          </button>
        )}
        {/* 消した aviutl_output は拾うワーカーがいないので再実行させない（サーバーも断る。Issue #998） */}
        {req.status === 'failed' && (req.kind ?? 'aviutl_output') !== 'aviutl_output' && (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => onRetry(req.id)}
            disabled={retrying === req.id}
            style={{ marginLeft: 4 }}
          >
            {retrying === req.id ? '...' : '再実行'}
          </button>
        )}
      </div>
      {req.progress && (
        <div style={{ fontSize: '0.85rem', color: '#8ab4f8', marginTop: 6 }}>{req.progress}</div>
      )}
      {req.error_message && (
        <div style={{ fontSize: '0.85rem', color: '#e89a9a', marginTop: 6 }}>{req.error_message}</div>
      )}
      {videoOpen && req.result_video_url && (
        <div style={{ marginTop: 12 }}>
          <video
            src={req.result_video_url}
            controls
            style={{ width: '100%', maxHeight: 480, background: '#000', borderRadius: 4 }}
          />
        </div>
      )}
    </div>
  );
}

const STATUS_FILTERS = [
  ['active',     '進行中'],
  ['all',        'すべて'],
  ['pending',    '待機中'],
  ['processing', '処理中'],
  ['completed',  '完了'],
  ['failed',     '失敗'],
  ['cancelled',  'キャンセル済'],
];

export default function ExportRequests() {
  usePageTitle('かき出し');
  const { tab } = useParams();
  const [requests, setRequests] = useState([]);
  const [allDuels, setAllDuels] = useState([]); // 試合・デュエルの名前の解決に使う
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [cancelling, setCancelling] = useState(null);
  const [retrying, setRetrying] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(() => {
    api.getExportRequests()
      .then(data => {
        setRequests(data);
        setLoading(false);
        const hasActive = data.some(r => r.status === 'pending' || r.status === 'processing');
        if (hasActive) {
          timerRef.current = setTimeout(load, 5000);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.project('GetDuelListProject').then(setAllDuels);
    return () => clearTimeout(timerRef.current);
  }, [load]);

  const handleCancel = async (reqId) => {
    setCancelling(reqId);
    try {
      await api.cancelExportRequest(reqId);
      clearTimeout(timerRef.current);
      load();
    } catch (err) {
      alert('キャンセルに失敗しました: ' + err.message);
    } finally {
      setCancelling(null);
    }
  };

  const handleRetry = async (reqId) => {
    setRetrying(reqId);
    try {
      await api.retryExportRequest(reqId);
      clearTimeout(timerRef.current);
      load();
    } catch (err) {
      alert('再実行に失敗しました: ' + err.message);
    } finally {
      setRetrying(null);
    }
  };

  const ofTab = requests.filter(r => inTab(tab, r.kind));
  const filtered = statusFilter === 'all'
    ? ofTab
    : statusFilter === 'active'
      ? ofTab.filter(isActive)
      : ofTab.filter(r => r.status === statusFilter);

  // game_id → 表示名のマップ（全デュエルのgamesを展開して作成）
  const gameMap = {};
  const duelMap = {};
  for (const d of allDuels) {
    duelMap[d.id] = d.name;
    for (const g of d.games || []) {
      gameMap[g.id] = g.name ? `${d.name} / ${g.name}` : d.name;
    }
  }

  // デュエル単位のkind（archive_duel。Issue #756 で消した duel_sources の過去の行も）はgame_idを持たないためデュエル名を出す
  // カードのジョブなどはどちらも持たない（名前は出さない）
  const requestLabel = (req) => (
    req.game_id != null
      ? (gameMap[req.game_id] ?? `game #${req.game_id}`)
      : req.duel_id != null ? (duelMap[req.duel_id] ?? `duel #${req.duel_id}`) : ''
  );

  if (!EXPORT_TABS.some(t => t.id === tab)) return <Navigate to="/exports/all" replace />;

  return (
    <>
    <nav className="subnav" aria-label="かき出し" data-testid="export-nav">
      {EXPORT_TABS.map(t => {
        const active = requests.filter(r => isActive(r) && inTab(t.id, r.kind)).length;
        return <NavLink key={t.id} to={`/exports/${t.id}`}>{t.label}{active > 0 && `（${active}）`}</NavLink>;
      })}
    </nav>
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">かき出し依頼</h1>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {STATUS_FILTERS.map(([key, label]) => (
          <button
            key={key}
            className={`btn btn-sm ${statusFilter === key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setStatusFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <p className="loading">読み込み中...</p>}
      {!loading && filtered.length === 0 && <p className="loading">依頼がありません</p>}

      {filtered.map(req => (
        <RequestCard
          key={req.id}
          req={req}
          duelName={requestLabel(req)}
          cancelling={cancelling}
          onCancel={handleCancel}
          retrying={retrying}
          onRetry={handleRetry}
        />
      ))}
    </div>
    </>
  );
}
